import * as React from 'react';

/**
 * A row leaving its list, visibly — the brief's "a row leaves when an edit stops it matching, and
 * that must visibly happen" (LST-06), for the one mock list whose membership an edit on it changes:
 * the initial review. A list is recomputed on every render,
 * so without this a row would simply vanish under the press that removed it; here it stays a
 * moment, marked and struck, with the reason said in a live region, and then goes.
 */

const LEAVE_MS = 1800;

interface IGhost {
  id: string;
  /** Why it leaves, after "Leaves the list: ". */
  reason: string;
}

/**
 * Rows watched as they are edited, and those an edit took off the list kept for `LEAVE_MS` as
 * ghosts. Only an edited row can leave this way — a changed filter or survey redraws the list at
 * once, since nothing about any one row changed. Checked in a layout effect, so the ghost is in
 * place before the frame that would otherwise have drawn the list without the row.
 */
export function useLeaving(
  members: readonly string[],
  describe: (id: string) => { name: string; reason: string }
) {
  const [ghosts, setGhosts] = React.useState<IGhost[]>([]);
  const [said, setSaid] = React.useState('');
  const watched = React.useRef<string[]>([]);
  const timers = React.useRef<number[]>([]);

  React.useEffect(() => () => timers.current.forEach((timer) => window.clearTimeout(timer)), []);

  React.useLayoutEffect(() => {
    if (watched.current.length === 0) return;
    const left = watched.current.filter((id) => !members.includes(id));
    watched.current = [];
    // An edit that took nobody off says nothing, and stops the last leaving being said as its.
    if (left.length === 0) {
      setSaid('');
      return;
    }
    const fresh = left.map((id) => ({ id, ...describe(id) }));
    setGhosts((current) => [
      ...current.filter((ghost) => !left.includes(ghost.id)),
      ...fresh.map(({ id, reason }) => ({ id, reason }))
    ]);
    setSaid(fresh.map(({ name, reason }) => `${name} leaves the list: ${reason}.`).join(' '));
    const timer = window.setTimeout(
      () => setGhosts((current) => current.filter((ghost) => !left.includes(ghost.id))),
      LEAVE_MS
    );
    timers.current.push(timer);
  });

  const leaving = new Map(
    ghosts.filter((ghost) => !members.includes(ghost.id)).map((ghost) => [ghost.id, ghost.reason])
  );
  const watch = (id: string) => {
    watched.current.push(id);
  };
  return { watch, leaving, said };
}

export function leavingRowProps(row: { leaving: Nullable<string> }): React.HTMLAttributes<HTMLTableRowElement> {
  if (!row.leaving) return {};
  const props: React.HTMLAttributes<HTMLTableRowElement> & { 'data-leaving': string } = {
    'data-leaving': '',
    className:
      'bg-[color-mix(in_srgb,var(--color-royal-blue)_5%,white)] shadow-[inset_3px_0_0_var(--veritant-royal-55)] transition-colors'
  };
  return props;
}

/** A value in a leaving row: struck, faded, and no longer a control. */
export function Struck({ children }: { children: React.ReactNode }) {
  return <s className="text-[var(--veritant-royal-70)] decoration-[var(--veritant-royal-55)] opacity-70">{children}</s>;
}

